import { type RequestFailure, type ApiResponse } from '@owl-nest/api-client'
import { api, type CreateOrder, type Order, type Project } from '@owl-nest/api-client/latest'
import * as Enum from '@owl-nest/legacy-enum'

import { type PromiseAction } from '../../requestMiddleware'
import { type State } from '../../types'

export const TYPE = Enum.create({
  CREATE_REQUEST: 'CREATE_REQUEST',
  CREATE_FAILURE: 'CREATE_FAILURE',
  CREATE_SUCCESS: 'CREATE_SUCCESS',

  GET_REQUEST: 'GET_REQUEST',
  GET_FAILURE: 'GET_FAILURE',
  GET_SUCCESS: 'GET_SUCCESS',

  RESET: 'RESET',

  RENEW_REQUEST: 'RENEW_REQUEST',
  RENEW_FAILURE: 'RENEW_FAILURE',
  RENEW_SUCCESS: 'RENEW_SUCCESS',
})
export type TYPE = Enum.ValueOf<typeof TYPE>

type CreateRequest = { type: typeof TYPE.CREATE_REQUEST; address: CreateOrder }
type CreateFailure = { type: typeof TYPE.CREATE_FAILURE; failure: RequestFailure }
type CreateSuccess = { type: typeof TYPE.CREATE_SUCCESS; success: Order }

type GetRequest = { type: typeof TYPE.GET_REQUEST; userId: number }
type GetFailure = { type: typeof TYPE.GET_FAILURE; error: RequestFailure }
type GetSuccess = { type: typeof TYPE.GET_SUCCESS; success: Order }

type Reset = { type: typeof TYPE.RESET }

type RenewRequest = { type: typeof TYPE.RENEW_REQUEST }
type RenewFailure = { type: typeof TYPE.RENEW_FAILURE; failure: RequestFailure }
type RenewSuccess = { type: typeof TYPE.RENEW_SUCCESS; success: Order }

type GetOptions = { skipLocalStorage?: boolean }

export type OrderAction =
  | CreateRequest
  | CreateFailure
  | CreateSuccess
  | GetRequest
  | GetFailure
  | GetSuccess
  | Reset
  | RenewRequest
  | RenewFailure
  | RenewSuccess

export function create(
  projectId: Project['id'],
  order: CreateOrder,
): PromiseAction<State<unknown>, ApiResponse<Order>> {
  return {
    types: [TYPE.CREATE_REQUEST, TYPE.CREATE_SUCCESS, TYPE.CREATE_FAILURE],
    promise: async (_, state) => {
      const identity = state.user.identity
      if (identity.type !== 'loggedin') {
        throw Error("can't create order if user is not loggedin")
      }
      const response = await api.post.projectOrders({
        urlParams: { projectId: String(projectId) },
        body: order,
      })

      return response.next((success) => success.body)
    },
  }
}

export function get(orderId: number, options: GetOptions): PromiseAction<State<unknown>, ApiResponse<Order>> {
  return {
    types: [TYPE.GET_REQUEST, TYPE.GET_SUCCESS, TYPE.GET_FAILURE],
    promise: async (_, state) => {
      const identity = state.user.identity

      if (identity.type !== 'loggedin') {
        throw Error("can't load order if user is not loggedin")
      }

      const response = await api.get.order({
        urlParams: { orderId: String(orderId) },
      })

      return response.next((success) => success.body as unknown as Order)
    },
    ...options,
  }
}

export function reset(): Reset {
  return {
    type: TYPE.RESET,
  }
}

export function renew(
  orderId: string | number,
  order: Partial<Order>,
): PromiseAction<State<unknown>, ApiResponse<{ order: Order; url: string }>> {
  return {
    types: [TYPE.RENEW_REQUEST, TYPE.RENEW_SUCCESS, TYPE.RENEW_FAILURE],
    promise: async (_, state) => {
      let paymentUrl: string

      const identity = state.user.identity
      if (identity.type !== 'loggedin') {
        throw Error("can't get order if user is not loggedin")
      }

      const response = await api.post.renewOrder({
        urlParams: { orderId: String(orderId) },
        body: order,
      })

      const getResponse = response.nextAsync(async (success) => {
        paymentUrl = success.body.payment_url
        return api.get.order({ urlParams: { orderId: String(orderId) } })
      })

      return (await getResponse).next((success) => {
        return { order: success.body as unknown as Order, url: paymentUrl }
      })
    },
  }
}
